import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { TranslocoDirective } from '@jsverse/transloco';
import { Organization } from '../../../core/models/organization.model';
import { Icon } from '../icon/icon';
import { tenantColor } from '../../utils/tenant-color';

function initials(name: string | undefined): string {
  if (!name) {
    return '?';
  }
  return name
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

interface TenantTile {
  id: string;
  name: string;
  alias?: string;
  initials: string;
  color: string;
}

interface TenantOption extends TenantTile {
  active: boolean;
}

/** One entry of the searchable panel list: a letter divider or a tenant row. */
type PanelRow =
  | { kind: 'letter'; label: string }
  | { kind: 'org'; tile: TenantOption };

/** How many tenants before the simple menu becomes the searchable panel. */
const SEARCHABLE_THRESHOLD = 5;
const RECENT_STORAGE_KEY = 'recentTenants';
const RECENT_LIMIT = 3;

function readRecentIds(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'string') : [];
  } catch {
    return [];
  }
}

/**
 * TenantSwitcher — the top-bar control (design system CMS navigation) for users
 * who belong to more than one organization. Shows the current tenant (a
 * colour-coded initial tile + name + tenant count) on the dark header, and opens
 * a white menu to switch. Collapses to a static header when there's one tenant.
 *
 * Two menu variants, chosen by list size:
 * - up to 5 tenants: the plain listbox;
 * - more than 5: the searchable panel from the CMS-shell design — search field,
 *   a "Recent" block (last picks, persisted to localStorage), the full list
 *   grouped by first letter, and a count footer with an optional jump to the
 *   admin back-office (canGoAdmin).
 *
 * Tile colour is derived from the org id (our Organization model carries no
 * brand colour); the "N tenants" sub-line stands in for the design's
 * region/display-count line, which we have no data for.
 */
@Component({
  selector: 'app-tenant-switcher',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Icon, NgTemplateOutlet, TranslocoDirective],
  templateUrl: './tenant-switcher.html',
  styles: `
    /* DS tenant menu rows: sunken fill for the selected row (not action-fill),
       text stays ink-1; the blue checkmark is the only selected indicator. */
    .ts-row {
      color: var(--ink-1);
    }
    .ts-row:hover {
      background: var(--surface-2);
    }
    .ts-row.active {
      background: var(--surface-sunken);
    }
    .ts-alias {
      color: var(--ink-3);
    }
  `,
  host: {
    '(document:mousedown)': 'onDocumentMouseDown($event)',
    '(document:keydown.escape)': 'close()',
  },
})
export class TenantSwitcher {
  private readonly host = inject(ElementRef<HTMLElement>);

  readonly organizations = input<Organization[]>([]);
  readonly current = input<Organization | null>(null);
  /** Show the "Admin · all tenants" footer link in the searchable panel. */
  readonly canGoAdmin = input(false);

  /** Emits the id of the organization the user chose to switch to. */
  readonly switchOrganization = output<string>();
  /** The footer link to the admin back-office was clicked. */
  readonly goAdmin = output<void>();

  protected readonly open = signal(false);
  protected readonly query = signal('');
  private readonly recentIds = signal<string[]>(readRecentIds());

  private readonly searchInput =
    viewChild<ElementRef<HTMLInputElement>>('searchInput');

  protected readonly single = computed(() => this.organizations().length <= 1);
  protected readonly searchable = computed(
    () => this.organizations().length > SEARCHABLE_THRESHOLD,
  );
  protected readonly count = computed(() => this.organizations().length);

  // Single source of the "which org is current" rule: the bound current(),
  // falling back to the first org. Both the trigger view and the option list
  // derive from this so they can't disagree.
  private readonly activeOrg = computed(
    () => this.current() ?? this.organizations()[0] ?? null,
  );

  private toTile(org: Organization): TenantOption {
    return {
      id: org.id,
      name: org.name,
      alias: org.alias,
      initials: initials(org.name),
      color: tenantColor(org.id || org.name),
      active: org.id === (this.activeOrg()?.id ?? null),
    };
  }

  protected readonly currentView = computed<TenantTile | null>(() => {
    const org = this.activeOrg();
    return org ? this.toTile(org) : null;
  });

  /** Plain menu (5 tenants or fewer), in the order the backend sent. */
  protected readonly tenantViews = computed<TenantOption[]>(() =>
    this.organizations().map((org) => this.toTile(org)),
  );

  private readonly sorted = computed<Organization[]>(() =>
    [...this.organizations()].sort((a, b) => a.name.localeCompare(b.name)),
  );

  private readonly matches = computed<Organization[]>(() => {
    const q = this.query().trim().toLowerCase();
    if (!q) {
      return this.sorted();
    }
    return this.sorted().filter((org) =>
      `${org.name} ${org.alias ?? ''}`.toLowerCase().includes(q),
    );
  });

  /** Searchable-panel list: letter dividers while browsing, flat when filtering. */
  protected readonly panelRows = computed<PanelRow[]>(() => {
    const filtering = this.query().trim().length > 0;
    const rows: PanelRow[] = [];
    let lastLetter: string | null = null;
    for (const org of this.matches()) {
      const letter = (org.name[0] ?? '#').toUpperCase();
      if (!filtering && letter !== lastLetter) {
        rows.push({ kind: 'letter', label: letter });
        lastLetter = letter;
      }
      rows.push({ kind: 'org', tile: this.toTile(org) });
    }
    return rows;
  });

  protected readonly recentViews = computed<TenantOption[]>(() => {
    const byId = new Map(this.organizations().map((org) => [org.id, org]));
    return this.recentIds()
      .map((id) => byId.get(id))
      .filter((org): org is Organization => !!org)
      .slice(0, RECENT_LIMIT)
      .map((org) => this.toTile(org));
  });

  protected readonly showRecent = computed(
    () => this.query().trim() === '' && this.recentViews().length > 0,
  );

  protected readonly matchCount = computed(() => this.matches().length);

  constructor() {
    // The search field only exists once the panel is rendered, so focus reacts
    // to the viewChild signal instead of racing the DOM after toggle().
    effect(() => {
      if (this.open()) {
        this.searchInput()?.nativeElement.focus();
      }
    });
  }

  protected toggle(): void {
    if (this.single()) {
      return;
    }
    this.query.set('');
    this.open.update((v) => !v);
  }

  protected close(): void {
    this.open.set(false);
  }

  protected onQueryInput(value: string): void {
    this.query.set(value);
  }

  protected select(orgId: string): void {
    this.open.set(false);
    if (orgId === this.activeOrg()?.id) {
      return;
    }
    // Persist before emitting: the parent reacts with a full-page OIDC
    // redirect, so nothing after the emit is guaranteed to run.
    this.rememberRecent(orgId);
    this.switchOrganization.emit(orgId);
  }

  protected onGoAdmin(): void {
    this.open.set(false);
    this.goAdmin.emit();
  }

  private rememberRecent(orgId: string): void {
    const ids = [orgId, ...this.recentIds().filter((id) => id !== orgId)].slice(
      0,
      RECENT_LIMIT,
    );
    this.recentIds.set(ids);
    try {
      localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(ids));
    } catch {
      // Storage unavailable — switching still works, recents just aren't kept.
    }
  }

  protected onDocumentMouseDown(event: MouseEvent): void {
    if (
      this.open() &&
      !this.host.nativeElement.contains(event.target as Node)
    ) {
      this.open.set(false);
    }
  }
}
